import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { filter } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AutoUnsubscriber } from '../../utility/helpers/mixins';
import { RegexPatterns } from '../../utility/helpers/regex';
import {
  IClientRegistration,
  IFormItem,
  IRegistrationForm,
  QuestionType
} from '../../utility/interfaces/registration-form';
import { HomeBuyerVM } from '../../utility/models/home-buyer';
import { ApiService } from '../../utility/services/api.service';
import { CommunityService } from '../../utility/services/community.service';
import { FavoritesService } from '../../utility/services/favorites.service';
import { HomeBuyerService } from '../../utility/services/home-buyer/home-buyer.service';
import { OverlayService, OverlayTypes } from '../../utility/services/overlay.service';
import { SalesRepresentativeService } from '../../utility/services/sales-representative.service';
import { SettingsService } from '../../utility/services/settings.service';
import { ThemeService } from '../../utility/services/theme.service';
import { enterLeaveFadeAnimation } from '../animations';
import { ToastService } from '../toast/toast.service';

import { CanadianProvinceList, CountryList, IHomeBuyer, UsStateList } from '@ml/common';

@Component({
  selector: 'home-buyer-registration',
  templateUrl: './home-buyer-registration.component.html',
  styleUrls: ['./home-buyer-registration.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [enterLeaveFadeAnimation()],
  standalone: false
})
export class HomeBuyerRegistrationComponent extends AutoUnsubscriber implements OnInit {
  isShowing = false;
  stickRight = false;
  communityLogo: string;
  message: string;
  isNewHomeBuyer: boolean;
  homeBuyer: HomeBuyerVM;
  salesRepId: string;
  regexPatterns = RegexPatterns;
  disclaimer: string;
  useSingleBox = false;
  customClientRegistration: IClientRegistration;
  allCustomRequiredQuestionsHaveValue = true;
  questionModel = new Object();
  requireExisting: IFormItem[];
  questionType = QuestionType;
  useCheckboxWithDisclaimer = false;
  disclaimerConsent = false;
  showFormError = false;
  selectedCountry: { FullName: string; Abreviation: string };
  externalRegistrationUrl = '';

  constructor(
    private cdr: ChangeDetectorRef,
    private overlay: OverlayService,
    private homeBuyerService: HomeBuyerService,
    private toaster: ToastService,
    private theme: ThemeService,
    private communityService: CommunityService,
    private salesRepService: SalesRepresentativeService,
    private settingsService: SettingsService,
    private favorites: FavoritesService,
    private apiService: ApiService
  ) {
    super();
  }

  ngOnInit() {
    this.communityLogo = this.theme.getCommunityLogo();
    this.disclaimer = this.settingsService
      .get('HomeBuyerDisclaimerText')
      ?.replace('{{currentYear}}', new Date().getFullYear());
    this.useSingleBox = this.settingsService.get('HomeBuyerRegistrationStyle') === 'Single box';

    this.externalRegistrationUrl = this.settingsService.get('BuyerRegistrationExternalUrl');

    this.addSub(
      this.overlay.state$
        .pipe(filter(state => state.targetedComponent === OverlayTypes.HomeBuyerRegistration))
        .subscribe(state => {
          this.isShowing = state.isShowing;
          this.stickRight = state.stickRight;
          if (this.isShowing) {
            if (this.externalRegistrationUrl) {
              this.toaster.showLoading();
            } else {
              this.initializeWelcomeScreen();
            }
          }

          this.cdr.detectChanges();
        })
    );

    this.addSub(
      this.salesRepService.current$.subscribe(salesRep => {
        this.salesRepId = salesRep ? salesRep.SalesRepresentativeId : null;
      })
    );

    if (!this.externalRegistrationUrl) {
      this.apiService.getWithNoAuth('/assets/config/form_config.json').then(questions => {
        const regForm: IRegistrationForm = JSON.parse(questions);
        this.customClientRegistration = regForm.Clients.find(
          x =>
            x.ClientId === this.communityService.current.ClientId ||
            x.ClientIds?.includes(this.communityService.current.ClientId)
        );

        this.requireExisting = this.customClientRegistration?.Existing || regForm.Default;
        this.useCheckboxWithDisclaimer = this.customClientRegistration?.UseCheckboxWithDisclaimer;

        if (!this.customClientRegistration) return;

        const countryQuestion = this.customClientRegistration.Questions.find(
          q => q.Name === 'Country'
        );
        if (countryQuestion) {
          countryQuestion.Options = CountryList.flatMap(c => c.FullName);
        }

        const neighborhoodsQuestion = this.customClientRegistration.Questions.find(
          q => q.Name === 'NeighborhoodsSelect'
        );
        if (neighborhoodsQuestion) {
          const neighborhoods = this.communityService.current.Neighborhoods;

          if (!environment.production && neighborhoods.length > 1) {
            neighborhoodsQuestion.Options = neighborhoods.map(n => n.PublicName || n.Name);
          } else {
            this.customClientRegistration.Questions =
              this.customClientRegistration.Questions.filter(q => q !== neighborhoodsQuestion);
          }
        }

        if (this.customClientRegistration.Questions.some(x => x.Required && !x.DefaultValue))
          this.allCustomRequiredQuestionsHaveValue = false;

        this.customClientRegistration.Questions?.forEach(x => {
          if (x.Type === QuestionType.MultiSelect) {
            this.questionModel[x.Name] = [];
          } else if (x.DefaultValue) {
            this.questionModel[x.Name] = x.DefaultValue;
          } else {
            this.questionModel[x.Name] = '';
          }
        });
      });
    }
  }

  private initializeWelcomeScreen() {
    this.isNewHomeBuyer = false;
    this.message = 'Enter your email to continue.';
    this.homeBuyer = new HomeBuyerVM();
    this.homeBuyer.ClientId = this.communityService.current.ClientId;
    this.homeBuyer.SalesRepresentativeId = this.salesRepId;
  }

  handleClose(evt?: Event) {
    if (evt) evt.stopPropagation();

    this.overlay.hide(OverlayTypes.HomeBuyerRegistration);
  }

  handleCheckEmailAddress(form: NgForm) {
    if (form.invalid) return;

    this.toaster.showLoading();
    this.homeBuyerService.get(this.homeBuyer.mapToEntity()).subscribe(
      (homeBuyer: IHomeBuyer) => {
        this.homeBuyerService.current = homeBuyer;
        this.handleClose();
        this.toaster.showInfo(`Welcome back, ${new HomeBuyerVM(homeBuyer).FullName}!`);
      },
      (error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.message = 'Welcome! Please complete your registration to continue.';
          this.isNewHomeBuyer = true;
          this.toaster.hideLoading();
          this.cdr.detectChanges();
        } else {
          this.toaster.showStickyError('Error looking up home buyer by email');
          console.error(`Error looking up home buyer by email: ${error}`);
        }
      }
    );
  }

  onEmailChange(email: string) {
    this.homeBuyer.Email = email;
    this.cdr.detectChanges();
  }

  onFirstNameChange(val: string) {
    this.homeBuyer.FirstName = val;
    this.cdr.detectChanges();
  }

  onLastNameChange(val: string) {
    this.homeBuyer.LastName = val;
    this.cdr.detectChanges();
  }

  onTelephoneChange(val: string) {
    this.homeBuyer.PhoneNumber = val;
    this.cdr.detectChanges();
  }

  onQuestionChange(val: string | string[], name: string) {
    this.questionModel[name] = val;

    if (name === 'Country') {
      const country = val as string;
      this.selectedCountry = CountryList.find(c => c.FullName === country);
      const countriesWithStates = ['United States', 'Canada'];
      const stateSelect = this.customClientRegistration.Questions.find(x => x.Name === 'State');
      if (stateSelect && countriesWithStates.includes(country)) {
        stateSelect.Options = this.getCountrySpecificStates(country);
        this.customClientRegistration.Questions.forEach(q => {
          q.Disabled = false;
          if (q.Name === 'Zip') q.Required = true;
        });
      } else {
        const namesToDisable = ['Zip', 'State'];
        this.customClientRegistration.Questions.forEach(q => {
          if (namesToDisable.includes(q.Name)) {
            q.Disabled = true;
            q.Required = false;
          }
        });
      }
    }

    this.triggerPhoneValidation();

    if (this.customClientRegistration) {
      this.allCustomRequiredQuestionsHaveValue = this.customClientRegistration.Questions.filter(
        x => x.Required
      ).every(x => this.hasValue(this.questionModel[x.Name]));
    }

    this.cdr.detectChanges();
  }

  private hasValue(val: string | string[]): boolean {
    return Array.isArray(val) ? val.length > 0 : !!val;
  }

  triggerPhoneValidation() {
    const phoneValue = this.homeBuyer.PhoneNumber;
    this.homeBuyer.PhoneNumber = phoneValue;
  }

  getCountrySpecificStates(country: string) {
    switch (country) {
      case 'Canada':
        return CanadianProvinceList.map(x => x.Abreviation);
      case 'USA':
      default:
        return UsStateList.map(x => x.Abreviation);
    }
  }

  handleCheckboxes(evt: any, name: string) {
    if (this.questionModel[name] === evt.target.value) {
      this.questionModel[name] = '';
    } else {
      this.questionModel[name] = evt.target.value;
    }

    const item = this.customClientRegistration?.Questions.find(q => q.Name === name);
    if (item) item.Invalid = false;

    if (this.customClientRegistration) {
      this.allCustomRequiredQuestionsHaveValue = this.customClientRegistration.Questions.filter(
        x => x.Required
      ).every(x => this.hasValue(this.questionModel[x.Name]));
    }

    this.cdr.detectChanges();
  }

  async handleRegister(form: NgForm) {
    if (form.pristine || form.invalid || !this.allCustomRequiredQuestionsHaveValue) {
      Object.keys(form.controls).forEach(key => {
        form.controls[key].markAsTouched();
      });

      this.customClientRegistration?.Questions.filter(
        q => q.Type === QuestionType.Checkbox && q.Required && !this.questionModel[q.Name]
      ).forEach(q => (q.Invalid = true));

      this.showFormError = true;
      this.cdr.detectChanges();
      return;
    }

    this.showFormError = false;
    this.toaster.showLoading();
    try {
      if (!this.homeBuyer.CustomData) this.homeBuyer.CustomData = {};
      this.homeBuyer.CustomData.DisclaimerConsent = this.disclaimerConsent;

      const savedHomeBuyer = await this.homeBuyerService.create(
        this.homeBuyer,
        this.favorites.current,
        this.questionModel
      );
      this.homeBuyerService.current = savedHomeBuyer;
      this.handleClose();
      // immediately save FavoritesSession to API for help with data reports
      await this.favorites.saveAsync(this.favorites.current);
      this.toaster.showInfo(`Welcome ${savedHomeBuyer.FullName}!`);

      // reset form and optional fields
      form.resetForm();
      this.homeBuyer = new HomeBuyerVM();
      this.questionModel = new Object();

      this.customClientRegistration?.Questions?.forEach(x => {
        if (x.DefaultValue) {
          this.questionModel[x.Name] = x.DefaultValue;
        } else {
          this.questionModel[x.Name] = '';
        }
        if (x.Type === QuestionType.Select) {
          this.questionModel[x.Name] = '';
        }
        if (x.Type === QuestionType.MultiSelect) {
          this.questionModel[x.Name] = [];
        }
      });
    } catch (err) {
      this.toaster.showStickyError('Error creating new home buyer');
      console.error(`Error creating new home buyer: ${err}`);
    }
  }

  handleConsentChecked(event: boolean): void {
    // right now only use case is Mattamy SMS consent
    // TODO: make this more abstract for other use cases if needed eventually

    const phoneField = this.requireExisting.find(x => x.Name.toLowerCase().includes('phone'));
    if (phoneField) {
      this.homeBuyer.CustomData.SMSConsent = event;

      phoneField.Required = event;
      if (phoneField.Required) {
        phoneField.Name += '*';
      } else {
        phoneField.Name = phoneField.Name.replace('*', '');
      }
    }
  }

  onIframeLoad() {
    this.toaster.hideLoading();
  }
}
